/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/DeleteAzureRMServiceEndpointConfirmationDialog" />
import * as React from "react";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { TitleSize } from "VSSUI/Header";
import { IVssPageContext } from "VSS/Platform/Context";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { ServiceEndpointOperationStatus } from "Constants";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { DelayedFunction } from "DistributedTask/Common/AadAuthorizer/DelayedFunction";
import { IServiceEndpointDetails } from "DistributedTaskUI/ServiceEndpoints/Details/Source/ServiceEndpointsSource";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

export interface IDeleteAzureRMServiceEndpointConfirmationDialogProps {
    onBackNavigation: () => void;
    onDismiss: () => void;
    serviceEndpoint: ServiceEndpoint;
}

export interface IDeleteAzureRMServiceEndpointConfirmationDialogState {
    errorMessage: string;
    isDeletionInProgress: boolean;
    isDeletionFailed: boolean;
    deepCleanEndpoint: boolean;
}

export class DeleteAzureRMServiceEndpointConfirmationDialog extends VssComponent<IDeleteAzureRMServiceEndpointConfirmationDialogProps, IDeleteAzureRMServiceEndpointConfirmationDialogState> {
    constructor(props: IDeleteAzureRMServiceEndpointConfirmationDialogProps, context: IVssComponentContext) {
        super(props, context);
        this._serviceEndpointActionCreator = ActionCreatorManager.CreateActionCreator<ServiceEndpointActionsCreator, IVssPageContext>(ServiceEndpointActionsCreator, "service-endpoints-section-actionscreator", this.context.pageContext);
        this.state = { errorMessage: "", isDeletionInProgress: false, isDeletionFailed: false, deepCleanEndpoint: true };
    }

    public render(): React.ReactNode {
        return (
            <Dialog
                titleProps={{ text: Resources.DeleteDialogTitle, size: TitleSize.Large }}
                onDismiss={() => { this.props.onDismiss(); }}
                footerButtonProps={[
                    {
                        text: Resources.Cancel,
                        onClick: () => {
                            this.props.onDismiss();
                        }
                    },
                    {
                        text: Resources.DeletePrimaryButtonText,
                        primary: true,
                        disabled: this.state.isDeletionInProgress,
                        onClick: () => {
                            this._deleteARMServiceConnection();
                        }
                    }
                ]}

            >
                {this.getARMDeletionDialogMessage()}
            </Dialog>
        );
    }

    private getARMDeletionDialogMessage(): JSX.Element {
        if (this.state.isDeletionInProgress) {
            return (
                <div className="service-endpoint-arm-deletion">
                    {Resources.CleaningServicePrincipleDetails}
                    <Spinner className="spinner" size={SpinnerSize.medium} />
                </div>
            );
        } else {
            let deletionMessage = this.state.isDeletionFailed ? Resources.DeleteARMEndpointDialogMessage : Resources.DeleteDialogMessage;
            return (
                <div>
                    {this._getErrorCardComponent()}
                    {deletionMessage}
                </div>
            );
        }
    }

    private _getErrorCardComponent(): React.ReactNode {
        if (this.state.errorMessage === "") {
            return (<div />);
        }

        return (
            <MessageCard className="service-endpoint-arm-deletion-error-card" onDismiss={() => { this.setState({ errorMessage: "" }); }} severity={MessageCardSeverity.Error}>
                {this.state.errorMessage}
            </MessageCard>
        );
    }

    private _deleteARMServiceConnection = (): void => {
        this._serviceEndpointActionCreator.deleteServiceEndpoint(this.props.serviceEndpoint.id, this.state.deepCleanEndpoint).then(() => {
            if (!this.state.deepCleanEndpoint) {
                this.props.onBackNavigation();
            } else {
                this.setState({ isDeletionInProgress: true });
                this.waitForSpnEndpointDelete(this.props.serviceEndpoint.id);
            }
        }, (err) => {
            this.setState({ errorMessage: err.message });
        });
    }

    public waitForSpnEndpointDelete(endpointId: string): void {
        let monitorSpnProgress = new DelayedFunction(this, 1000, "monitorSpnProgress", () => {
            this._serviceEndpointActionCreator.getServiceEndpointDetails(endpointId).then((serviceEndpointDetails: IServiceEndpointDetails) => {
                if (serviceEndpointDetails.serviceEndpoint) {
                    let operationStatus = serviceEndpointDetails.serviceEndpoint.operationStatus;
                    if (operationStatus.state === ServiceEndpointOperationStatus.Failed) {
                        // raise error and set deepCleanEndpoint to false so that when next delete happens, we do not try to clean SPN
                        this.setState({ errorMessage: operationStatus.statusMessage, isDeletionInProgress: false, isDeletionFailed: true, deepCleanEndpoint: false });
                    }

                    if (operationStatus.state === ServiceEndpointOperationStatus.InProgress) {
                        monitorSpnProgress.reset();
                    }

                } else {
                    // delete successful
                    this.props.onBackNavigation();
                }
            });
        });

        monitorSpnProgress.start();
    }

    private _serviceEndpointActionCreator: ServiceEndpointActionsCreator;
}
